import { withoutCloverFormMetadata } from 'utils/payments';
import { generatePath } from 'react-router-dom';
import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { apiQuery } from 'store/query';
import { addParamsToUrl } from 'utils/query';
import { CloverVoid, CloverPayment, PaymentIntent } from 'types';

import { cartApi, tagTypes as cartTagTypes } from '../cart';
import { eventApi, tagTypes as eventTagTypes } from '../event';
import { payoutApi, tagTypes as payoutTagTypes } from '../payout';
import { analyticsApi, tagTypes as analyticsTagTypes } from '../analytics';
import { accountingApi, tagTypes as accountingTagTypes } from '../accounting';
import {
  commissionLevelApi,
  tagTypes as commissionLevelTagTypes,
} from '../commission-level';

import {
  Tip,
  DeviceId,
  TokenInfo,
  TipRequest,
  MakeSaleRequest,
  AddTokenRequest,
  SignatureResponse,
  TokenEcommerceInfo,
  DisplayOrderRequest,
  PrintReceiptRequest,
  DisplayMessageRequest,
  VoidCloverPaymentRequest,
  CloverVoidPaymentRequest,
  PrintPaymentReceiptRequest,
} from './types';

export const tagTypes = ['Clover'];

export const cloverApi = createApi({
  tagTypes,
  baseQuery: apiQuery,
  reducerPath: 'cloverApi',
  endpoints: (build) => ({
    tokenInfo: build.query<TokenInfo, void>({
      providesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'get',
        url: URL.CLOVER_TOKEN_INFO,
      }),
    }),

    requestTip: build.mutation<Tip, TipRequest>({
      invalidatesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.CLOVER_REQUEST_TIP,
      }),
    }),

    addToken: build.mutation<TokenInfo, AddTokenRequest>({
      invalidatesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.CLOVER_ADD_TOKEN,
      }),
    }),

    displayThanks: build.query<void, DeviceId>({
      providesTags: tagTypes,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.CLOVER_DISPLAY_THANKS, params),
      }),
    }),

    resetDevice: build.mutation<void, DeviceId>({
      invalidatesTags: tagTypes,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.CLOVER_RESET_DEVICE, params),
      }),
    }),

    cancelAction: build.mutation<void, DeviceId>({
      invalidatesTags: tagTypes,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.CLOVER_CANCEL_ACTION, params),
      }),
    }),

    printReceipt: build.mutation<void, PrintReceiptRequest>({
      invalidatesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.CLOVER_PRINT_RECEIPT,
      }),
    }),

    tokenEcommerce: build.query<TokenEcommerceInfo, void>({
      providesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'get',
        url: URL.CLOVER_GET_ECOMMERCE_TOKEN,
      }),
    }),

    updateToken: build.mutation<TokenInfo, AddTokenRequest>({
      invalidatesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'patch',
        url: URL.CLOVER_UPDATE_TOKEN,
      }),
    }),

    displayWelcome: build.mutation<void, DeviceId>({
      invalidatesTags: tagTypes,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.CLOVER_DISPLAY_WELCOME, params),
      }),
    }),

    displayMessage: build.mutation<void, DisplayMessageRequest>({
      invalidatesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.CLOVER_DISPLAY_MESSAGE,
      }),
    }),

    signature: build.mutation<SignatureResponse, DeviceId>({
      invalidatesTags: tagTypes,
      query: (params) => ({
        method: 'post',
        url: addParamsToUrl(URL.CLOVER_SIGNATURE, params),
      }),
    }),

    verifyDeviceConnect: build.query<void, DeviceId>({
      providesTags: tagTypes,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.CLOVER_VERIFY_DEVICE_CONNECT, params),
      }),
    }),

    addEcommerceToken: build.mutation<TokenEcommerceInfo, AddTokenRequest>({
      invalidatesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.CLOVER_ADD_ECOMMERCE_TOKEN,
      }),
    }),

    updateEcommerceToken: build.mutation<TokenEcommerceInfo, AddTokenRequest>({
      invalidatesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'patch',
        url: URL.CLOVER_UPDATE_ECOMMERCE_TOKEN,
      }),
    }),

    displayOrder: build.mutation<CloverPayment, DisplayOrderRequest>({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data: withoutCloverFormMetadata(data),
        method: 'post',
        url: generatePath(URL.CLOVER_DISPLAY_ORDER, { id: String(id) }),
      }),
    }),

    printPaymentReceipt: build.mutation<void, PrintPaymentReceiptRequest>({
      invalidatesTags: tagTypes,
      query: ({ checkoutId, ...data }) => ({
        data,
        method: 'post',
        url: generatePath(URL.CLOVER_PRINT_PAYMENT_RECEIPT, {
          checkoutId: String(checkoutId),
        }),
      }),
    }),

    makeSale: build.mutation<CloverPayment, MakeSaleRequest>({
      invalidatesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.CLOVER_MAKE_SALE,
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(cartApi.util.invalidateTags(cartTagTypes));
        } catch (error) {
          console.error(error);
        }
      },
    }),

    initiatePayment: build.mutation<PaymentIntent, MakeSaleRequest>({
      invalidatesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.CLOVER_INITIATE_PAYMENT,
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(cartApi.util.invalidateTags(cartTagTypes));
        } catch (error) {
          console.error(error);
        }
      },
    }),

    cancelPayment: build.mutation<PaymentIntent, number>({
      invalidatesTags: tagTypes,
      query: (id) => ({
        method: 'post',
        url: generatePath(URL.CLOVER_CANCEL_PAYMENT, { intentId: String(id) }),
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(cartApi.util.invalidateTags(cartTagTypes));
        } catch (error) {
          console.error(error);
        }
      },
    }),

    voidCloverPayment: build.mutation<CloverVoid, VoidCloverPaymentRequest>({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'post',
        url: generatePath(URL.VOID_CLOVER_PAYMENT, { paymentId: id }),
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(cartApi.util.invalidateTags(cartTagTypes));
        } catch (error) {
          console.error(error);
        }
      },
    }),

    voidPayment: build.mutation<CloverVoid, CloverVoidPaymentRequest>({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'post',
        url: generatePath(URL.CLOVER_VOID_PAYMENT, { checkoutId: String(id) }),
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(eventApi.util.invalidateTags(eventTagTypes));
          dispatch(payoutApi.util.invalidateTags(payoutTagTypes));
          dispatch(accountingApi.util.invalidateTags(accountingTagTypes));
          dispatch(analyticsApi.util.invalidateTags(analyticsTagTypes));
          dispatch(
            commissionLevelApi.util.invalidateTags(commissionLevelTagTypes)
          );
        } catch (error) {
          console.error(error);
        }
      },
    }),
  }),
});

export const {
  useTokenInfoQuery,
  useMakeSaleMutation,
  useAddTokenMutation,
  useSignatureMutation,
  useDisplayThanksQuery,
  useLazyTokenInfoQuery,
  useRequestTipMutation,
  useResetDeviceMutation,
  useUpdateTokenMutation,
  useVoidPaymentMutation,
  useTokenEcommerceQuery,
  useDisplayOrderMutation,
  useCancelActionMutation,
  usePrintReceiptMutation,
  useCancelPaymentMutation,
  useDisplayMessageMutation,
  useDisplayWelcomeMutation,
  useInitiatePaymentMutation,
  useVerifyDeviceConnectQuery,
  useVoidCloverPaymentMutation,
  useAddEcommerceTokenMutation,
  usePrintPaymentReceiptMutation,
  useUpdateEcommerceTokenMutation,
} = cloverApi;
